'use client';

import { Dialog, DialogPanel } from '@headlessui/react';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';

import { Icons } from '@monoimin/atoms';

import PaginationCarousel from './PaginationCarousel';
import { slideToActiveParams } from './carousel.types';
import { CarouselButton } from './CarouselButton';

export interface FullscreenCarouselProps {
  slides?: ReactNode[];
  onChangeActiveSlide?: (activeIndex: number) => void;
  onClose?: () => void;
  initialActive?: number;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  panelClassName?: string;
  contentAfterSlider?: (
    active: number,
    slideToActive: (param: slideToActiveParams) => void
  ) => any;
}

export const FullscreenCarousel = ({
  slides,
  onChangeActiveSlide,
  panelClassName,
  initialActive,
  contentAfterSlider,
  isOpen,
  setIsOpen,
  onClose,
}: FullscreenCarouselProps) => {
  const slidesCount = slides?.length ?? 1;

  const haveSlides = Array.isArray(slides) && slides.length > 0;

  const [active, setActive] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const slideToActive = useCallback(
    (param: slideToActiveParams, behavior: 'smooth' | 'instant' = 'smooth') => {
      if (containerRef.current) {
        const container = containerRef.current;
        const scrollStep = container.scrollWidth / slidesCount;
        let scrollTo = container.scrollWidth;
        if (param === 'next') {
          scrollTo = container?.scrollLeft + scrollStep;
        }
        if (param === 'prev') {
          scrollTo = container?.scrollLeft - scrollStep;
        }
        if (typeof param === 'number') {
          scrollTo = scrollStep * param;
        }
        container.scrollTo({
          left: scrollTo,
          behavior: behavior,
        });
      }
    },
    [containerRef, slidesCount]
  );

  const slideToNext = useCallback(() => slideToActive('next'), []);
  const slideToPrev = useCallback(() => slideToActive('prev'), []);

  useEffect(() => {
    onChangeActiveSlide?.(active);
  }, [onChangeActiveSlide, active]);

  useEffect(() => {
    typeof initialActive === 'number' &&
      slideToActive(initialActive, 'instant');
  }, [slideToActive, initialActive]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.stopPropagation();
        slideToPrev();
      }
      if (event.key === 'ArrowRight') {
        event.stopPropagation();
        slideToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const handleOnScroll = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;

      const scrollStep = Math.floor(container.scrollWidth / slidesCount);
      const scrollLeftVal =
        container.scrollLeft - (container.scrollLeft % scrollStep);
      const newActive = scrollLeftVal / scrollStep;
      if (active !== newActive) {
        setActive(newActive);
      }
    }
  }, [containerRef, active, slidesCount, initialActive]);

  const onCloseModal = () => {
    setIsOpen(false);

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onCloseModal} className='relative z-[501]'>
      <div
        data-testid='fullscreen-carousel-overlay'
        className='fixed inset-0 backdrop-blur-xs bg-black-500/60'
        aria-hidden='true'
      />
      <div className='fixed w-screen inset-0 flex items-center justify-center cursor-pointer z-10'>
        <button
          className='rounded-full bg-black/40 hover:bg-black/100 flex items-center justify-center absolute right-5 top-5 duration-150 ease-in-out shrink-0 w-12 h-12'
          onClick={onCloseModal}
          aria-label='close'
        >
          <Icons
            icon='close'
            isInteractive
            className='bg-white'
            sizeClassName='h-6 w-6'
          />
        </button>
        <DialogPanel
          ref={containerRef}
          onScroll={handleOnScroll}
          className={clsx(
            'mx-auto flex gap-5 overflow-y-hidden overflow-x-scroll snap-mandatory snap-x scrollbar-hide cursor-default w-[90vw] sm:w-[80vw] h-[80vh]',
            panelClassName
          )}
        >
          {haveSlides && active > 0 && (
            <CarouselButton
              onClick={slideToPrev}
              direction='left'
              className='left-5 absolute bg-black/40 hover:bg-black/100 duration-150 easy-in-out -translate-y-1/2 top-1/2 hidden sm:flex'
            />
          )}
          {haveSlides && active < slides.length - 1 && (
            <CarouselButton
              onClick={slideToNext}
              direction='right'
              className='right-5 absolute bg-black/40 hover:bg-black/100 duration-150 easy-in-out -translate-y-1/2 top-1/2 hidden sm:flex'
            />
          )}
          {slides?.map((slide, index) => {
            return (
              <div
                key={index}
                className='snap-start inline-flex min-w-[100%] z-10'
              >
                {slide}
              </div>
            );
          })}
          {haveSlides && (
            <div className='absolute bottom-5 z-20 right-1/2 translate-x-1/2'>
              <PaginationCarousel
                max={slides.length}
                active={active}
                slideToActive={slideToActive}
              />
            </div>
          )}
          {contentAfterSlider?.(active, slideToActive)}
        </DialogPanel>
      </div>
    </Dialog>
  );
};
